// ============================================================
// SettlementPreview.tsx — the live deductions preview (Stage 4; layout =
// the chosen mockup, option A + option C's breakdown bar).
// POST /settlement/preview runs the tested engine (settlementMath) on the
// saved inputs + the section's unsaved edits; it writes nothing.
//
// (Settlement redesign) THE ONE MONEY SUMMARY on the screen, reading top down:
//   the headline — "Travis would get £9,430" ("gets" once issued)
//   the deductions, each with a short "why" (and what it was cut from, struck
//   through, when a cap bit), then any credit — directly under the headline
//   anything paid out already (CRU, interim payments, retention)
//   Total deductions · anything held · Damages, with its heads
// plus "How it was worked out", folded: "Where the £X goes" — a horizontal bar
// of the same split (breakdown.ts) with a legend giving every amount AS TEXT —
// the server's deductions breakdown, and the engine's own rows and rules.
// The options a preview can be run with live in the figures drawer, folded
// (PreviewOptionsFold), and are never saved.
//
// Kept: "DRAFT — not for client use" (header + net tile); the greyed-out
// "out of date" state when the latest run fails; blocking errors (red) and
// warnings (amber), the child / protected-party message said ONCE; the
// "preview only, not saved" settlement date; the unreviewed-CFA flag.
// When the section is narrow (CaseSettlementSection SETTLEMENT_TWO_COL_MIN) it shows <SettlementBottomBar> instead — Net to
// client + "Show breakdown", which opens this same preview in a modal.
//
// (Batch 2) Runs on the SAVED figures plus the one card being edited — the
// "Includes unsaved changes" badge says so while a card is open. The CFA
// check is asked for ONCE, on the CFA card: here only a short line pointing
// there (and the engine's CFA_NOT_REVIEWED warning is not repeated). Nothing to
// work out yet (no CFA, no damages) is calm grey text, not an error: the next
// step is the page's one main button. PAYEE-AWARE: for a
// child / protected party the net figure is labelled by where it goes
// (netLabelOf — "To the Court Funds Office"), "(proposed)" until a court
// order is recorded on the statement.
// ============================================================
import React, { useId } from 'react'
import DateInput from '../shared/DateInput'
import ModalFrame from '../shared/ModalFrame'
import { BIcon, CARD, LABEL, NOTE, INPUT, money, Skel, Spinner, B_PRIMARY } from '../billing/billingUi'
import { DraftBadge, PenceField, Banner } from './settlementUi'
import { breakdownSegments, segmentWidthPct, type SegmentKey } from './breakdown'
import { CFA_STAGE_LABEL, type NetDestination, type PreviewOptions, type PreviewResult, type WaterfallRow } from '../../types/settlement.types'
import { netSumLabel, type Capacity, type RepresentativeType } from '../../types/settlementStatement.types'

// Who the net sum goes to, for the labels (the statement's own rule —
// backend lib/settlementStatement defaultDestination — unless the preview's
// option or the draft statement says otherwise).
export interface Payee {
  statementDestination?: NetDestination | null
  representativeType?: RepresentativeType | null
  representativeNamed?: boolean
  courtOrderRecorded?: boolean
}
export const payeeDestination = (capacity: Capacity, chosen: NetDestination | undefined, p: Payee | undefined): NetDestination => {
  if (chosen) return chosen
  if (p?.statementDestination) return p.statementDestination
  if (capacity === 'CHILD') return 'COURT_FUNDS_OFFICE'
  if (capacity === 'PROTECTED_PARTY') {
    return p?.representativeNamed && (p.representativeType === 'DEPUTY' || p.representativeType === 'LPA_ATTORNEY') ? 'DEPUTY' : 'COURT_FUNDS_OFFICE'
  }
  return 'CLIENT'
}
// "Net to client" for an adult; "To the Court Funds Office (proposed)" for a
// child before the court order, without "(proposed)" once it is recorded.
export const netLabelOf = (capacity: Capacity | null | undefined, chosen: NetDestination | undefined, p: Payee | undefined): string => {
  if (!capacity || capacity === 'ADULT') return 'Net to client'
  const label = netSumLabel(payeeDestination(capacity, chosen, p), p?.representativeType)
  return p?.courtOrderRecorded ? label : `${label} (proposed)`
}

// The badge on the preview while a card's draft is included.
export const UnsavedBadge: React.FC = () => (
  <span role="status" className="inline-flex items-center gap-[5px] text-[11px] font-semibold px-2 py-[2px] rounded-[5px] whitespace-nowrap border border-casevo-border bg-casevo-input text-casevo-text-soft">
    <i className="w-[6px] h-[6px] rounded-full bg-casevo-text-soft" aria-hidden="true" />Includes unsaved changes
  </span>
)

const SECTION_LABEL: Record<WaterfallRow['section'], string> = {
  COSTS: 'Base-costs position', CREDIT: 'Credited to the client',
  DAMAGES: 'Damages', LESS: 'Less', CAP: 'Caps and limits', DEDUCTION: 'Deductions', TOTAL: 'Totals',
}
const DEST_LABEL: Record<NetDestination, string> = { CLIENT: 'Paid to the client', COURT_FUNDS_OFFICE: 'Court Funds Office', DEPUTY: 'Court of Protection deputy', CHILD_ACCOUNT: 'The child’s own account' }

// Bar colours (casevo-* tokens). The legend carries every amount as text.
export const SEGMENT_CLASS: Record<SegmentKey, string> = {
  CRU: 'bg-casevo-text-soft/60',
  INTERIM: 'bg-casevo-light/70',
  RETENTION: 'bg-casevo-mid/40',
  SUCCESS_FEE: 'bg-casevo-gold',
  COUNSEL: 'bg-casevo-gold/45',
  SHORTFALL: 'bg-casevo-gold/70',
  ATE: 'bg-casevo-warn/80',
  DISBURSEMENTS: 'bg-casevo-warn/50',
  ADVERSE: 'bg-casevo-danger/70',
  NET: 'bg-casevo-primary',
}

const pounds0 = (pence: number): string => `£${Math.round(pence / 100).toLocaleString('en-GB')}`

interface Props {
  preview: PreviewResult | null
  loading: boolean
  error: string | null
  options: PreviewOptions
  setOptions: (o: PreviewOptions) => void
  today: string
  // A migrated CFA nobody has reviewed yet → amber flag in the net tile.
  cfaUnreviewed?: boolean
  onReviewCfa?: () => void
  // Bits of the CURRENT figures the story names (from the page's drafts).
  heads?: { pslaPence: number; pastLossPence: number; futureLossPence: number; interestPence: number }
  firmCapLabel?: string | null // e.g. "30%" when the CFA has a firm cap
  // (Batch 2) A card's unsaved draft is in this run.
  unsaved?: boolean
  // Nothing to work out yet: this, in place of the money.
  empty?: string | null
  // (Batch 2) Payee-aware labels for a child / protected party.
  payee?: Payee
  // (Settlement redesign) "Travis would get" / "Travis gets" over the net figure.
  headline?: string
  // These are an issued statement's frozen figures: no draft mark, no editing.
  frozen?: boolean
  // Held in client account while a dispute stands.
  heldPence?: number | null
  // Opens the figures drawer on a line's card; absent while the figures are locked.
  onEdit?: (card: MoneyEditTarget) => void
  // Opens the drawer on all the figures (read-first).
  onOpenFigures?: () => void
  // "How these deductions were worked out" (the server's breakdown).
  worked?: React.ReactNode
  // Warnings already on the approver's checklist are not said twice.
  hideCodes?: string[]
}
export type MoneyEditTarget = 'damages' | 'cru' | 'costs' | 'ate' | 'court' | 'interims'
const EDIT_OF: Record<string, MoneyEditTarget> = { SUCCESS_FEE: 'costs', COUNSEL_SUCCESS_FEE: 'costs', SHORTFALL: 'costs', ATE_PREMIUM: 'ate', DISBURSEMENTS: 'ate', ADVERSE_COSTS_OFFSET: 'court' }

const ProposedTag: React.FC = () => (
  <span className="inline-flex items-center text-[10.5px] font-bold uppercase tracking-[0.06em] px-2 py-[2px] rounded-[4px] bg-casevo-warn-bg text-casevo-warn whitespace-nowrap">
    Proposed — subject to court order
  </span>
)

// "Where the £X goes" — the bar + its legend.
export const BreakdownBar: React.FC<{ figures: Parameters<typeof breakdownSegments>[0] }> = ({ figures }) => {
  const { totalPence, barTotalPence, segments } = breakdownSegments(figures)
  if (totalPence <= 0) return null
  return (
    <figure className="m-0 mt-[14px]" aria-label={`Where the ${pounds0(totalPence)} goes`}>
      <figcaption className="text-[12.5px] font-semibold text-casevo-forest mb-[6px]">Where the {pounds0(totalPence)} goes</figcaption>
      <div className="flex h-[14px] rounded-[7px] overflow-hidden gap-[2px] bg-casevo-border-soft" aria-hidden="true">
        {segments.map((s) => <span key={s.key} className={`h-full ${SEGMENT_CLASS[s.key]}`} style={{ width: `${segmentWidthPct(s.pence, barTotalPence)}%` }} />)}
      </div>
      <dl className="m-0 mt-[8px] grid grid-cols-[repeat(auto-fill,minmax(168px,1fr))] gap-x-[18px] gap-y-[4px] text-[12px]">
        {segments.map((s) => (
          <div key={s.key} className="flex items-center gap-[6px] min-w-0">
            <span aria-hidden="true" className={`w-[9px] h-[9px] rounded-[2px] shrink-0 ${SEGMENT_CLASS[s.key]}`} />
            <dt className="text-casevo-text-soft truncate">{s.label}</dt>
            <dd className={`m-0 ml-auto tabular-nums ${s.key === 'NET' ? 'font-semibold text-casevo-forest' : 'text-casevo-forest'}`}>{pounds0(s.pence)}</dd>
          </div>
        ))}
      </dl>
    </figure>
  )
}

// A plain-English "why" for a deduction row, plus what cut it (if a cap bit).
const cutReason = (rule: string): string =>
  /Statutory cap/i.test(rule) ? 'the statutory cap'
    : /Firm total cap/i.test(rule) ? 'the firm’s own cap'
      : /CPR 21/i.test(rule) || /Court-approved/i.test(rule) ? 'the court limit for children'
        : /damages available/i.test(rule) ? 'what’s left of the damages'
          : /counselInCap|firm bears/i.test(rule) ? 'the CFA (not passed on)'
            : rule
const whyOf = (row: WaterfallRow, ctx: { ratePct: number; capBase: number; capPct: string }): string => {
  const cuts = Array.from(new Set(row.reductions.map((r) => cutReason(r.rule))))
  const cut = cuts.length ? `, cut by ${cuts.join(' and ')}` : ''
  switch (row.key) {
    case 'SUCCESS_FEE': return `${ctx.ratePct}% uplift on ${/recovered base/.test(row.rule) ? 'the base costs recovered' : 'all the firm’s base costs'}${row.reductions.some((r) => /Statutory cap/i.test(r.rule)) ? `, capped at ${ctx.capPct} of ${money(ctx.capBase)}` : ''}${cuts.filter((c) => c !== 'the statutory cap').length ? `, cut by ${cuts.filter((c) => c !== 'the statutory cap').join(' and ')}` : ''}`
    case 'COUNSEL_SUCCESS_FEE': return `Counsel’s uplift, passed on under the CFA${cut}`
    case 'SHORTFALL': return `The firm’s costs not covered by the other side${/interim/.test(row.rule) ? ' or by interim bills' : ''}${cut}`
    case 'ATE_PREMIUM': return `Insurance premium the other side didn’t pay${cut}`
    case 'DISBURSEMENTS': return `Expenses the other side didn’t pay back${cut}`
    case 'ADVERSE_COSTS_OFFSET': return `Costs the court ordered the client to pay (QOCS)${cut}`
    default: return row.rule
  }
}
const CONFIRM_WITH_SOLICITOR = 'Confirm with solicitor.'
const STORY_LABEL: Record<string, string> = {
  SUCCESS_FEE: 'Success fee', COUNSEL_SUCCESS_FEE: 'Counsel’s success fee', SHORTFALL: 'Costs not recovered',
  ATE_PREMIUM: 'ATE premium', DISBURSEMENTS: 'Unrecovered disbursements', ADVERSE_COSTS_OFFSET: 'Adverse costs (QOCS)',
}

const StoryRow: React.FC<{ title: React.ReactNode; why?: React.ReactNode; amount: string; was?: string | null; credit?: boolean; strong?: boolean; onEdit?: () => void; editLabel?: string }> = ({ title, why, amount, was, credit, strong, onEdit, editLabel }) => (
  <div className="flex flex-wrap items-start justify-between gap-x-[12px] py-[8px] border-b border-casevo-border-soft last:border-b-0" data-money-row="">
    <div className="min-w-0 flex-[1_1_7rem]">
      <div className={`text-[14px] ${strong ? 'font-semibold' : ''} ${credit ? 'text-casevo-brand dark:text-casevo-link' : 'text-casevo-forest'}`}>
        {title}
        {onEdit && <button type="button" aria-label={editLabel} className="ml-[6px] text-[12px] underline underline-offset-2 text-casevo-link focus:outline-none casevo-focus rounded-[4px]" onClick={onEdit}>Edit</button>}
      </div>
      {why && <div className="text-[12px] text-casevo-text-soft leading-snug mt-[1px]">{why}</div>}
    </div>
    <div className="ml-auto text-right min-w-0 [overflow-wrap:anywhere]">
      <div className={`text-[14px] ${strong ? 'font-semibold' : ''} tabular-nums ${credit ? 'text-casevo-brand dark:text-casevo-link' : 'text-casevo-forest'}`}>{amount}</div>
      {was && <div className="text-[11.5px] tabular-nums text-casevo-text-soft line-through">{was}</div>}
    </div>
  </div>
)

// The options a preview can be tried with (never saved). The statement's own
// details are set on the statement; this is for "what if".
export const PreviewOptionsFold: React.FC<Pick<Props, 'options' | 'setOptions' | 'today'> & { used: PreviewResult['used'] | null; changed?: boolean; onReset?: () => void }> = ({ options, setOptions, today, used, changed, onReset }) => {
  const uid = useId()
  const ratePct = used ? used.rate.rateBasisPoints / 100 : 0
  return (
    <details className="rounded-[12px] border border-casevo-border bg-casevo-surface px-[14px] py-[10px]" open={changed || undefined} data-preview-options="">
      <summary className="cursor-pointer text-[13px] font-semibold text-casevo-forest focus:outline-none casevo-focus rounded-[4px]">Try different options (not saved)</summary>
      <div className="flex flex-col gap-[12px] mt-[12px]">
        {/* Date + kind share a line when there's room; the kind wraps under the date in a narrow column. */}
        <div className="flex flex-wrap gap-[12px]">
        <div className="w-[236px] shrink-0">
          <span className={LABEL}>Settlement date</span>
          <DateInput value={options.settlementDate ?? today} allowFuture ariaLabel="Settlement date" onChange={(v) => setOptions({ ...options, settlementDate: v ?? undefined })} />
          {/* Emptied → back to today (shown, never a blank box the preview ignores). */}
          <p className={`${NOTE} mt-[5px]`}>Preview only, not saved</p>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className={LABEL} htmlFor={`${uid}-kind`}>Settlement</label>
          <select id={`${uid}-kind`} className={INPUT} value={options.kind ?? 'FINAL'} onChange={(e) => setOptions({ ...options, kind: e.target.value as 'FINAL' | 'INTERIM' })}>
            <option value="FINAL">Final</option>
            <option value="INTERIM">Interim</option>
          </select>
        </div>
        </div>
        {options.kind === 'INTERIM'
          ? <div><PenceField label="Retention (held back)" pence={options.retentionPence ?? 0} onPence={(p) => setOptions({ ...options, retentionPence: p ?? 0 })} /></div>
          : (
            <div>
              <label className={LABEL} htmlFor={`${uid}-dest`}>Net damages go to</label>
              <select id={`${uid}-dest`} className={INPUT} value={options.netDestination ?? ''} onChange={(e) => setOptions({ ...options, netDestination: (e.target.value || undefined) as NetDestination | undefined })}>
                <option value="">Automatic</option>
                {(Object.keys(DEST_LABEL) as NetDestination[]).map((d) => <option key={d} value={d}>{DEST_LABEL[d]}</option>)}
              </select>
              {/* The explanation lives under the box, so the box never truncates it. */}
              {!options.netDestination && <p className={`${NOTE} mt-[5px]`}>A child’s damages go to the Court Funds Office; anyone else’s to the client.</p>}
            </div>
          )}
        <label className="flex items-center gap-[8px] text-[13px] text-casevo-forest">
          <input type="checkbox" className="accent-casevo-primary" checked={!!options.shortfallWaived} onChange={(e) => setOptions({ ...options, shortfallWaived: e.target.checked })} />
          Waive the base-costs shortfall for this settlement
        </label>
        {used && (
          <p className={`${NOTE} m-0`}>
            Success rate used: <b className="font-semibold text-casevo-forest">{ratePct}%</b> · {CFA_STAGE_LABEL[used.cfaStage].toLowerCase()}
            {used.rate.fromStage && used.rate.fromStage !== used.cfaStage ? ` (the ${CFA_STAGE_LABEL[used.rate.fromStage].toLowerCase()} rate)` : ''}
          </p>
        )}
        {changed && onReset && <button type="button" className="self-start text-[12.5px] font-semibold text-casevo-link underline underline-offset-2 focus:outline-none casevo-focus rounded-[4px]" onClick={onReset}>Back to the statement’s own details</button>}
      </div>
    </details>
  )
}

// The preview's body (no card chrome) — shared by the money column and the
// figures drawer's "Show breakdown" modal.
export const PreviewBody: React.FC<Props> = ({ preview, loading, error, options, cfaUnreviewed, onReviewCfa, heads, firmCapLabel, empty, payee, headline, frozen, heldPence, onEdit, worked, hideCodes }) => {
  if (empty) return <p className={`${NOTE} m-0`} data-money-empty="">{empty}</p>
  const r = preview?.result ?? null
  // The last run failed: what's on screen no longer matches the inputs.
  const stale = !!error && !!r
  const staleCls = stale ? 'opacity-40 grayscale' : ''
  // Deductions are "proposed" until the court order is recorded.
  const proposed = !!r && r.capacity !== 'ADULT' && !payee?.courtOrderRecorded
  const netLabel = netLabelOf(r?.capacity, options.kind === 'INTERIM' ? undefined : options.netDestination, payee)
  const net = r?.figures.netToClientPence ?? null
  const f = r?.figures
  const ratePct = preview ? preview.used.rate.rateBasisPoints / 100 : 0
  const capRow = r?.rows.find((x) => x.key === 'STATUTORY_CAP')
  const capPct = capRow?.label.match(/\((\d+%)\)/)?.[1] ?? '25%'
  const deductionRows = r ? r.rows.filter((x) => x.section === 'DEDUCTION' && x.finalPence > 0) : []
  const creditRow = r?.rows.find((x) => x.key === 'COSTS_CREDIT')
  const disbursementCreditRow = r?.rows.find((x) => x.key === 'DISBURSEMENT_CREDIT')
  const cruRow = r?.rows.find((x) => x.key === 'CRU')
  const interimRow = r?.rows.find((x) => x.key === 'INTERIM_PAYMENTS')
  const paidOut = (cruRow?.finalPence ?? 0) > 0 || (f?.interimPaymentsPence ?? 0) > 0 || (f?.retentionPence ?? 0) > 0
  return (
    <>
      {error && (
        <div className="mb-3">
          <Banner tone="error">
            {error}
            {stale && <span className="block mt-[2px] text-[12px]">The figures below are out of date until this is fixed.</span>}
          </Banner>
        </div>
      )}

      {/* The headline. Same-size placeholder while the first run loads. */}
      <div className="min-h-[72px]">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-casevo-text-soft" data-testid="net-label">{netLabel}{stale ? ' · out of date' : ''}</div>
        <div className={`mt-[2px] flex flex-wrap items-baseline gap-x-[8px] transition-opacity ${staleCls}`}>
          {headline && <span className="text-[20px] font-semibold text-casevo-forest" data-money-headline="">{headline}</span>}
          <span className="text-[30px] leading-[1.15] font-semibold tabular-nums text-casevo-forest [overflow-wrap:anywhere]" data-money-net="">
            {net != null ? money(net) : preview && !preview.ok ? '—' : <span className="inline-block h-[28px] w-40 rounded-md bg-casevo-border-soft motion-safe:animate-pulse align-middle" />}
          </span>
        </div>
        {/* (Batch 2) The CFA check is asked for on the CFA card — one short line here. */}
        {cfaUnreviewed && (
          <p className="m-0 mt-[6px] text-[12.5px] text-casevo-text-soft flex flex-wrap items-center gap-x-[8px]">
            <span>CFA terms not checked yet.</span>
            {onReviewCfa && <button type="button" className="underline underline-offset-2 font-semibold text-casevo-link focus:outline-none casevo-focus rounded-[4px]" onClick={onReviewCfa}>Go to the CFA</button>}
          </p>
        )}
      </div>

      {/* Only what can't be worked out is loud; a warning is calm information. */}
      {preview && preview.errors.length > 0 && (
        <div className="mt-3 flex flex-col gap-[8px]">
          {preview.errors.map((i, n) => <Banner key={`e${n}`} tone="error"><b className="font-semibold">Can’t calculate yet.</b> {i.message}</Banner>)}
        </div>
      )}
      {proposed && <p className={`${NOTE} mt-3 mb-0`}>This settlement is for a {r!.capacity === 'CHILD' ? 'child' : 'protected party'}, so the court must approve it. Every deduction is <b className="font-semibold">proposed — subject to court order</b> (CPR 21).</p>}

      {/* The money, top down. */}
      {!preview && !error ? (
        <div className="flex flex-col mt-[8px]" aria-hidden="true">
          {[1, 2, 4].map((n, g) => (
            <React.Fragment key={g}>
              <Skel className="h-[12px] w-40 mt-[18px] mb-[8px]" />
              {Array.from({ length: n }, (_, i) => <Skel key={i} className="h-[40px] w-full mb-[8px]" />)}
            </React.Fragment>
          ))}
          <Skel className="h-[44px] w-full mt-[10px] rounded-[10px]" />
        </div>
      ) : r && f ? (
        <div className={`mt-[8px] ${stale ? staleCls : loading ? 'opacity-60' : ''} transition-opacity`}>
          {deductionRows.length === 0
            ? <p className={`${NOTE} py-[8px] m-0`}>Nothing is deducted.</p>
            : deductionRows.map((row) => (
              <StoryRow key={row.key}
                title={<span className="inline-flex flex-wrap items-center gap-[6px]">{STORY_LABEL[row.key] ?? row.label}{proposed && <ProposedTag />}</span>}
                why={whyOf(row, { ratePct, capBase: f.capBasePence, capPct })}
                amount={`−${money(row.finalPence)}`}
                was={row.reductions.length ? `−${money(row.amountPence)}` : null}
                onEdit={onEdit && EDIT_OF[row.key] ? () => onEdit(EDIT_OF[row.key]) : undefined} editLabel={`Edit ${STORY_LABEL[row.key] ?? row.label}`} />
            ))}
          {creditRow && (
            <StoryRow credit title={creditRow.label} amount={`+${money(creditRow.finalPence)}`}
              why={f.costsCreditToNetPence ? `Clears the deductions; ${money(f.costsCreditToNetPence)} is added to the net sum` : 'Paid on interim bills and covered by the other side too'} />
          )}
          {disbursementCreditRow && (
            <StoryRow credit title={disbursementCreditRow.label} amount={`+${money(disbursementCreditRow.finalPence)}`}
              why={`Paid on interim bills and paid by the other side too. ${CONFIRM_WITH_SOLICITOR}`} />
          )}
          <StoryRow strong title="Total deductions" amount={`−${money(f.totalDeductionsPence)}`}
            why={f.firmCapPence != null ? `Within the firm’s cap of ${money(f.firmCapPence)}${firmCapLabel ? ` (${firmCapLabel})` : ''}` : proposed ? 'Proposed — the court decides' : undefined} />
          {(heldPence ?? 0) > 0 && (
            <div className="flex flex-wrap justify-between gap-x-[12px] py-[8px] border-b border-casevo-border-soft text-[14px] text-casevo-warn" data-money-held="">
              <span className="min-w-0 flex-[1_1_7rem]">Held (disputed, from the deductions)</span><span className="ml-auto tabular-nums [overflow-wrap:anywhere]">{money(heldPence ?? 0)}</span>
            </div>
          )}
          {paidOut && (
            <>
              {(cruRow?.finalPence ?? 0) > 0 && (
                <StoryRow title="CRU repaid" amount={`−${money(cruRow!.finalPence)}`} was={cruRow!.reductions.length ? `−${money(cruRow!.amountPence)}` : null}
                  why={cruRow!.reductions.length ? 'Benefits repaid to the DWP — only up to the matching past loss' : 'Benefits repaid to the DWP from the matching past loss'}
                  onEdit={onEdit ? () => onEdit('cru') : undefined} editLabel="Edit CRU" />
              )}
              {f.interimPaymentsPence > 0 && (
                <StoryRow title={interimRow && /^(\d+) payment/.test(interimRow.rule) && Number(interimRow.rule.match(/^(\d+)/)![1]) > 1 ? 'Interim payments' : 'Interim payment'}
                  amount={`−${money(f.interimPaymentsPence)}`} why="Already paid to the client" onEdit={onEdit ? () => onEdit('interims') : undefined} editLabel="Edit interim payments" />
              )}
              {f.retentionPence > 0 && <StoryRow title="Retention" amount={`−${money(f.retentionPence)}`} why="Held back from this interim settlement" />}
            </>
          )}
          <StoryRow title="Total damages" amount={money(f.totalDamagesPence)}
            why={heads ? [
              heads.pslaPence > 0 && `PSLA ${pounds0(heads.pslaPence)}`, heads.pastLossPence > 0 && `past loss ${pounds0(heads.pastLossPence)}`,
              heads.futureLossPence > 0 && `future loss ${pounds0(heads.futureLossPence)}`, heads.interestPence > 0 && `interest ${pounds0(heads.interestPence)}`,
            ].filter(Boolean).join(' · ') : undefined}
            onEdit={onEdit ? () => onEdit('damages') : undefined} editLabel="Edit damages" />

          <div className="mt-[10px] flex flex-col gap-[8px] empty:hidden">
            {/* The court-approval note is said once, in the line below; the CFA check once, on the CFA card; the approver's notes once, on their checklist. */}
            {(preview?.warnings ?? []).filter((i) => !(proposed && i.code === 'COURT_APPROVAL_REQUIRED') && i.code !== 'CFA_NOT_REVIEWED' && !(hideCodes ?? []).includes(i.code)).map((i, n) => (
              <p key={`w${n}`} className="m-0 rounded-[10px] bg-casevo-input px-[12px] py-[8px] text-[12.5px] text-casevo-forest" style={{ whiteSpace: 'pre-line' }} data-money-warning="">{i.message}</p>
            ))}
          </div>
          {/* One click away: the split, the server's breakdown, the engine's own rows + rules. */}
          <details className="mt-[12px] rounded-[10px] border border-casevo-border-soft px-[12px] py-[8px] group" data-how-worked-out="">
            <summary className="cursor-pointer text-[13px] font-semibold text-casevo-text-soft focus:outline-none casevo-focus rounded-[4px] list-none">
              <span className="inline-flex items-center gap-[4px]"><BIcon name="chevr" size={12} className="transition-transform group-open:rotate-90" />How it was worked out</span>
            </summary>
            <BreakdownBar figures={f} />
            {worked && <div className="mt-[12px]">{worked}</div>}
            <details className="mt-[12px]">
              <summary className="cursor-pointer text-[12.5px] font-semibold text-casevo-brand dark:text-casevo-link focus:outline-none casevo-focus rounded-[4px]">Full calculation</summary>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[12.5px] mt-[6px]">
                  <tbody>
                    {r.rows.map((row, i) => {
                      const head = i === 0 || r.rows[i - 1].section !== row.section
                      return (
                        <React.Fragment key={row.key}>
                          {head && <tr><td colSpan={2} className="pt-[10px] pb-[3px] text-[10.5px] font-bold uppercase tracking-[0.12em] text-casevo-text-soft">{SECTION_LABEL[row.section]}</td></tr>}
                          <tr className="align-top">
                            <td className="py-[5px] pr-[10px] border-b border-casevo-border-soft">
                              <div className="text-casevo-forest">{row.label}</div>
                              <div className="text-[11.5px] text-casevo-text-soft">{row.rule}</div>
                              {row.reductions.map((red, n) => <div key={n} className="text-[11.5px] text-casevo-text-soft">− {money(red.pence)} · {red.rule}</div>)}
                            </td>
                            <td className="py-[5px] border-b border-casevo-border-soft text-right tabular-nums whitespace-nowrap text-casevo-forest">
                              {row.key === 'COSTS_POSITION' && row.amountPence < 0 ? `−${money(-row.amountPence)}` : row.reductions.length || row.section === 'TOTAL' ? money(row.finalPence) : money(row.amountPence)}
                            </td>
                          </tr>
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
                <p className={`${NOTE} mt-[8px]`}>Engine {r.engineVersion}. Figures are rounded down in the client’s favour.{frozen ? ' These are the figures as issued.' : ' Draft only — not for client use.'}</p>
              </div>
            </details>
          </details>
        </div>
      ) : (
        <p className={`${NOTE} mt-[12px]`}>Fix the problem above to see the deductions.</p>
      )}
    </>
  )
}

// The money CARD: the one summary, for the whole life of the case.
const SettlementPreview: React.FC<Props> = (props) => (
  <section className={CARD} aria-label="Settlement preview" aria-busy={props.loading || undefined}>
    <div className="flex items-center flex-wrap gap-[8px] px-5 pt-4 pb-[8px]">
      <h3 className="m-0 flex-1 min-w-[80px] text-[11px] font-bold uppercase tracking-[0.14em] text-casevo-text-soft">The money</h3>
      {props.loading && <Spinner />}
      {props.unsaved && <UnsavedBadge />}
      {!props.frozen && <DraftBadge />}
    </div>
    <div className="px-5 pb-5">
      <PreviewBody {...props} />
      {props.onOpenFigures && <button type="button" className="mt-[10px] text-[13px] font-semibold text-casevo-link underline underline-offset-2 focus:outline-none casevo-focus rounded-[4px]" onClick={props.onOpenFigures}>{props.onEdit ? 'All the figures' : 'See the figures'}</button>}
    </div>
  </section>
)

// The figures drawer's foot: the net, updating live beside the field being
// changed, + "Show breakdown" (the full summary in a modal).
export const SettlementBottomBar: React.FC<Props & { open: boolean; setOpen: (o: boolean) => void }> = ({ open, setOpen, ...props }) => {
  const r = props.preview?.result ?? null
  const stale = !!props.error && !!r
  const netLabel = netLabelOf(r?.capacity, props.options.kind === 'INTERIM' ? undefined : props.options.netDestination, props.payee)
  return (
    <>
      {/* Sticky is applied by the parent wrapper (it must span the column for sticky to engage); the modal is portalled out of that stacking context. */}
      <div className="mt-4 -mx-4 sm:mx-0 border-t sm:border border-casevo-border bg-casevo-surface sm:rounded-[14px] shadow-[0_-6px_18px_rgba(16,42,29,0.08)] dark:shadow-[0_-6px_18px_rgba(0,0,0,0.3)] px-5 py-[10px] flex items-center gap-[16px] flex-wrap" role="region" aria-label="Settlement summary">
        <div className={stale ? 'opacity-40 grayscale' : ''}>
          <div className="flex items-center flex-wrap gap-x-[10px] gap-y-[4px]"><span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-casevo-text-soft" data-testid="bar-net-label">{netLabel}{stale ? ' · out of date' : ''}</span>{!props.frozen && <DraftBadge />}{props.unsaved && <UnsavedBadge />}</div>
          <div className="flex items-baseline flex-wrap gap-x-[12px]">
            <span className="text-[24px] leading-tight font-semibold tabular-nums text-casevo-forest">{r ? money(r.figures.netToClientPence) : '—'}</span>
            {r && <span className="text-[13px] text-casevo-text-soft tabular-nums">Deductions {money(r.figures.totalDeductionsPence)}</span>}
          </div>
        </div>
        <div className="ml-auto">
          <button type="button" className={B_PRIMARY} onClick={() => setOpen(true)}><BIcon name="chev" size={14} className="rotate-180" />Show breakdown</button>
        </div>
      </div>
      <ModalFrame isOpen={open} onClose={() => setOpen(false)} size="lg" portal eyebrow="Settlement" title="Deductions preview" subtitle={props.frozen ? 'The figures as issued.' : `Live, from the saved figures${props.unsaved ? ' and the card you’re editing' : ''} — draft, not for client use.`}>
        <PreviewBody {...props} />
      </ModalFrame>
    </>
  )
}

export default SettlementPreview
